import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import type { DsIcon } from '@bonniernews/dn-design-system-web/types-lib/ds-icon.d.ts'

import { IconUse } from '@bonniernews/dn-design-system-web/components/icon-sprite/icon-sprite.tsx'
import { Spinner } from '@bonniernews/dn-design-system-web/components/spinner/spinner.tsx'

export interface TextButtonToggleProps extends SharedProps {
  /** Whether the button is selected */
  selected?: boolean
  /** The text to display on the button */
  text: string
  /** The text to display when the button is selected */
  selectedText: string
  /** For all available icons see: https://designsystem.dn.se/?path=/story/foundations-icons--all-icons */
  iconName: DsIcon
  /** For all available icons see: https://designsystem.dn.se/?path=/story/foundations-icons--all-icons */
  selectedIconName: DsIcon
  /** Note: only works on button-tag, not on a-tag */
  disabled?: boolean
  /**  Ex. { target: "_blank", "data-test": "lorem ipsum" } */
  loading?: boolean
  /** Button type */
  type?: 'button' | 'submit' | 'reset'
  /** Fixed pixel value is used for typography to prevent scaling based on html font-size */
  forcePx?: boolean
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/text-button-toggle](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/text-button-toggle)
 * - Storybook: [TextButtonToggle](https://designsystem.dn.se/?path=/docs/basic-buttons-textbuttontoggle--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/text-button-toggle/text-button-toggle.scss'`
 */
export const TextButtonToggle = ({
  selected = false,
  text,
  selectedText,
  iconName,
  selectedIconName,
  disabled = false,
  loading = false,
  type = 'button',
  classNames,
  attributes = {},
  forcePx = false,
}: TextButtonToggleProps) => {
  const componentClassName = 'ds-text-btn-toggle'
  const ariaLabel = (attributes['aria-label'] as string) || text

  const classes = formatClassString([
    componentClassName,
    forcePx && 'force-px',
    loading && 'ds-loading',
    selected && `${componentClassName}--selected`,
    classNames,
  ])

  return (
    <button
      type={type}
      role='switch'
      aria-label={ariaLabel}
      aria-checked={selected}
      className={classes}
      disabled={disabled}
      data-force-px={forcePx}
      {...attributes}
    >
      <span className={`${componentClassName}__off`} aria-hidden='true'>
        <IconUse iconName={iconName} />
        <span className={`${componentClassName}__text`}>{text}</span>
      </span>
      <span className={`${componentClassName}__on`} aria-hidden='true'>
        <IconUse iconName={selectedIconName} />
        <span className={`${componentClassName}__text`}>{selectedText}</span>
      </span>
      <Spinner size='small' variant='primary' forcePx={forcePx} aria-hidden='true' />
    </button>
  )
}
